import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
  HostListener,
} from '@angular/core';
import { IconsModule } from '../../../icons/icons.module';
import {
  NgbDropdownModule,
  NgbModal,
  NgbModalModule,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap';
import IZikataClientProfile, {
  UserInfoConfigService,
  UserInfoConfig,
  SenderId,
  Organization,
  Member,
} from '../../../services/user-info-config/user-info-config.service';
import { Observable, BehaviorSubject, combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { SenderStateService } from '../../../services/sender-state/sender-state.service';
import { OrgStateService } from '../../../services/organization-state/org-state.service';

interface ViewModel {
  config: UserInfoConfig;
  orgs: Organization[];
  selectedOrg: Organization | null;
  isMobile: boolean;
}

@Component({
  selector: 'zikatagov-user-info',
  template: `
    <!-- ─────────────────────────────────────────────────────
         MOBILE COMPACT BAR  (used in the fixed header on mobile)
         Shows: Org dropdown | Sender ID dropdown | Balance chip | Top-Up btn
         ───────────────────────────────────────────────────── -->
    <ng-container *ngIf="mobileCompactMode && (viewModel$ | async) as vm">
      <div class="mobile-compact-bar">

        <!-- Org dropdown -->
        <div *ngIf="vm.config.showSenderIds" ngbDropdown class="compact-dropdown">
          <button class="btn btn-sm btn-outline-light dropdown-btn" id="orgDropdownMC" ngbDropdownToggle>
            <span class="dropdown-text">{{ vm.selectedOrg?.name || 'Org' }}</span>
            <ng-icon name="saxArrowDown1Outline" size="12"></ng-icon>
          </button>
          <div ngbDropdownMenu aria-labelledby="orgDropdownMC" class="dropdown-menu-custom">
            <button
              *ngFor="let option of vm.orgs"
              (click)="orgSelected(option)"
              ngbDropdownItem
              class="dropdown-item-custom"
            >{{ option.name }}</button>
          </div>
        </div>

        <!-- Sender ID dropdown -->
        <div *ngIf="vm.config.showSenderIds" ngbDropdown class="compact-dropdown">
          <button class="btn btn-sm btn-outline-light dropdown-btn" id="senderDropdownMC" ngbDropdownToggle>
            <span class="dropdown-text">{{ currentSender?.senderName || 'Sender ID' }}</span>
            <ng-icon name="saxArrowDown1Outline" size="12"></ng-icon>
          </button>
          <div ngbDropdownMenu aria-labelledby="senderDropdownMC" class="dropdown-menu-custom">
            <button
              *ngFor="let option of vm.selectedOrg?.senderIds ?? []"
              (click)="senderIdSelected(option)"
              ngbDropdownItem
              class="dropdown-item-custom"
            >{{ option.senderName }}</button>
          </div>
        </div>

        <!-- Balance chip -->
        <div *ngIf="vm.config.showBalance" class="balance-chip" (click)="loadBalance()">
          <small>Units</small>
          <span>{{ currentSenderIdBalance ?? '—' }}</span>
          <small *ngIf="currentSender?.creditLimit" class="credit-label">Credit: {{ currentSender.creditLimit | number }}</small>
        </div>

        <!-- Top Up -->
        <button
          *ngIf="vm.config.showTopUpButton"
          (click)="openTopUpModal(currentSender)"
          type="button"
          class="topup-btn-compact"
        >
          Top Up
        </button>

      </div>
    </ng-container>

    <!-- ─────────────────────────────────────────────────────
         FULL / DESKTOP VIEW  (original layout)
         ───────────────────────────────────────────────────── -->
    <div
      class="user-info-wrapper"
      *ngIf="!mobileCompactMode && (viewModel$ | async) as vm"
    >
      <!-- Mobile view toggle button -->
      <button
        *ngIf="vm.isMobile"
        class="btn btn-sm btn-link d-md-none ms-auto mobile-menu-toggle"
        (click)="toggleMobileMenu()"
      >
        <ng-icon name="saxMenu1Outline" size="20" class="text-white"></ng-icon>
      </button>

      <!-- Desktop view or expanded mobile menu -->
      <div
        [class.d-none]="vm.isMobile && !showMobileMenu"
        [class.mobile-menu]="vm.isMobile && showMobileMenu"
        class="user-info-container"
        [class.mobile-layout]="vm.isMobile"
      >
        <!-- Notification icon -->
        <div class="notification-section">
          <ng-icon name="saxNotificationOutline" class="notification-icon" size="22"></ng-icon>
        </div>

        <div class="divider" *ngIf="!vm.isMobile"></div>

        <!-- ORG dropdown -->
        <div *ngIf="vm.config.showSenderIds" ngbDropdown class="dropdown-section">
          <button class="btn btn-sm btn-outline-light dropdown-btn" id="orgDropdown" ngbDropdownToggle>
            {{ vm.selectedOrg?.name || 'Select Organisation' }}
          </button>
          <div ngbDropdownMenu aria-labelledby="orgDropdown" class="dropdown-menu-custom">
            <button *ngFor="let option of vm.orgs" (click)="orgSelected(option)" ngbDropdownItem class="dropdown-item-custom">
              {{ option.name }}
            </button>
          </div>
        </div>

        <!-- Sender ID dropdown -->
        <div *ngIf="vm.config.showSenderIds" ngbDropdown class="dropdown-section">
          <button class="btn btn-sm btn-outline-light dropdown-btn" id="senderDropdown" ngbDropdownToggle>
            {{ currentSender?.senderName || 'Select a Sender ID' }}
          </button>
          <div ngbDropdownMenu aria-labelledby="senderDropdown" class="dropdown-menu-custom">
            <button *ngFor="let option of vm.selectedOrg?.senderIds ?? []" (click)="senderIdSelected(option)" ngbDropdownItem class="dropdown-item-custom">
              {{ option.senderName }}
            </button>
          </div>
        </div>

        <div class="divider" *ngIf="!vm.isMobile && vm.config.showBalance"></div>

        <!-- Balance section -->
        <div *ngIf="vm.config.showBalance" class="balance-section">
          <div class="balance-content">
            <div class="balance-labels">
              <small class="balance-label">Balance</small>
              <span class="balance-value">Units: {{ currentSenderIdBalance }}</span>
              <small *ngIf="currentSender?.creditLimit" class="credit-limit-label">Credit Limit: {{ currentSender.creditLimit | number }}</small>
            </div>
            <i (click)="loadBalance()" class="fa fa-refresh balance-refresh" aria-hidden="true"></i>
          </div>
        </div>

        <div class="divider" *ngIf="!vm.isMobile && vm.config.showTopUpButton"></div>

        <!-- Top up button -->
        <div *ngIf="vm.config.showTopUpButton" class="topup-section">
          <button (click)="openTopUpModal(currentSender)" type="button" class="btn btn-sm btn-outline-light topup-btn">
            Top Up
          </button>
        </div>

        <div class="divider divider-main" *ngIf="!vm.isMobile"></div>

        <!-- User info section -->
        <div class="user-profile-section">
          <div class="profile-text">
            <span class="profile-name">{{ userProfile?.firstName ?? member?.firstName }}</span>
            <small class="profile-role">{{ member?.role?.toLowerCase() }}</small>
          </div>
          <div class="avatar-circle" [style.width.px]="avatarSize" [style.height.px]="avatarSize">
            <span class="avatar-initials">{{ getInitials() }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal template -->
    <ng-template #content let-modal>
      <div class="modal-header-custom">
        <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss('Cross click')"></button>
      </div>
      <div class="modal-body-custom">
        <h2 class="modal-title">M-Pesa Paybill</h2>
        <h6 class="modal-subtitle">Payment Instructions for {{ currentSender?.senderName }} Top Up</h6>
        <div class="modal-instructions">
          <ol class="instructions-list">
            <li>Go to Safaricom Menu</li>
            <li>Select M-Pesa</li>
            <li>Choose Lipa na M-Pesa</li>
            <li>Select Paybill</li>
            <li>Enter Paybill Number: <span class="fw-bold">4118635</span></li>
            <li>Enter Account Number: <span class="fw-bold">{{ currentSender?.senderName }}</span></li>
            <li>Enter Amount</li>
            <li>Enter PIN</li>
            <li>Complete Transaction</li>
          </ol>
        </div>
      </div>
    </ng-template>
  `,
  standalone: true,
  imports: [CommonModule, IconsModule, NgbDropdownModule, NgbModalModule],
  styles: [`
    :host {
      display: block;
      width: 100%;
    }

    /* ══════════════════════════════════════════
       MOBILE COMPACT BAR
       ══════════════════════════════════════════ */

    .mobile-compact-bar {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      width: 100%;
      flex-wrap: nowrap;
    }

    /* Compact dropdowns */
    .compact-dropdown {
      flex: 1;
      min-width: 0;
      display: flex;
    }

    .compact-dropdown .dropdown-btn {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.25rem;
      font-size: 0.775rem;
      font-weight: 500;
      padding: 0.45rem 0.625rem;
      border-radius: 8px;
      border: 1.5px solid #ede9fb;
      background: #faf8ff;
      color: #374151;
      white-space: nowrap;
      transition: all 0.2s;
      min-width: 0;
    }

    .compact-dropdown .dropdown-btn:active {
      background: #f3eeff;
      border-color: #c4b5fd;
      color: #7c3aed;
    }

    .compact-dropdown .dropdown-text {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 1;
      min-width: 0;
    }

    /* Balance chip */
    .balance-chip {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background: #f5f3ff;
      border: 1.5px solid #ede9fb;
      border-radius: 8px;
      padding: 0.35rem 0.625rem;
      flex-shrink: 0;
      cursor: pointer;
      transition: background 0.2s;
      -webkit-tap-highlight-color: transparent;
    }

    .balance-chip:active {
      background: #ede9fb;
    }

    .balance-chip small {
      font-size: 0.6rem;
      color: #7c6fa0;
      text-transform: uppercase;
      font-weight: 600;
      letter-spacing: 0.4px;
      line-height: 1;
    }

    .balance-chip span {
      font-size: 0.82rem;
      font-weight: 700;
      color: #1a003d;
      line-height: 1.35;
    }

    /* Compact top-up button */
    .topup-btn-compact {
      background: linear-gradient(135deg, #7c3aed, #a78bfa);
      color: #ffffff;
      border: none;
      border-radius: 8px;
      padding: 0.45rem 0.875rem;
      font-size: 0.8rem;
      font-weight: 600;
      letter-spacing: 0.3px;
      cursor: pointer;
      flex-shrink: 0;
      transition: opacity 0.2s, transform 0.15s;
      -webkit-tap-highlight-color: transparent;
      white-space: nowrap;
    }

    .topup-btn-compact:active {
      opacity: 0.85;
      transform: scale(0.96);
    }

    /* Shared dropdown menu */
    .dropdown-menu-custom {
      min-width: 180px;
      padding: 0.5rem 0;
      border: 1.5px solid #f0eafa;
      box-shadow: 0 4px 16px rgba(109, 40, 217, 0.1);
      border-radius: 10px;
    }

    .dropdown-item-custom {
      padding: 0.625rem 1rem;
      border: none;
      background: none;
      text-align: left;
      width: 100%;
      cursor: pointer;
      transition: background-color 0.2s ease;
      font-size: 0.875rem;
      color: #374151;
    }

    .dropdown-item-custom:hover {
      background-color: #f5f3ff;
      color: #7c3aed;
    }

    /* ══════════════════════════════════════════
       FULL (DESKTOP / ORIGINAL) LAYOUT
       ══════════════════════════════════════════ */

    .user-info-wrapper {
      display: flex;
      align-items: center;
      width: 100%;
    }

    .user-info-container {
      display: flex;
      align-items: center;
      gap: 1.25rem;
      width: 100%;
    }

    .user-info-container.mobile-layout {
      flex-direction: column;
      align-items: stretch;
      gap: 1.5rem;
    }

    .divider {
      width: 1px;
      height: 32px;
      background: #e5e7eb;
      flex-shrink: 0;
    }

    .divider-main {
      height: 40px;
      background: #d1d5db;
    }

    .notification-section {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .notification-icon {
      color: #7c3aed;
      cursor: pointer;
      transition: color 0.2s ease;
    }

    .notification-icon:hover {
      color: #6d28d9;
    }

    .dropdown-section {
      display: flex;
      align-items: center;
      flex-shrink: 0;
    }

    .dropdown-btn {
      padding: 0.5rem 1rem;
      white-space: nowrap;
      font-size: 0.875rem;
      font-weight: 500;
      letter-spacing: 0.3px;
      border-radius: 8px;
      transition: all 0.2s ease;
    }

    .balance-section {
      display: flex;
      align-items: center;
      flex-shrink: 0;
    }

    .balance-content {
      display: flex;
      align-items: center;
      gap: 1rem;
    }

    .balance-labels {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    .balance-label {
      font-size: 0.75rem;
      color: #6b7280;
      font-weight: 500;
      letter-spacing: 0.3px;
      text-transform: uppercase;
    }

    .balance-value {
      font-size: 0.9rem;
      font-weight: 600;
      color: #1a003d;
    }

    .balance-refresh {
      cursor: pointer;
      font-size: 1.1rem;
      transition: transform 0.3s ease, color 0.2s ease;
      color: #7c3aed;
    }

    .balance-refresh:hover {
      transform: rotate(180deg);
      color: #6d28d9;
    }

    .credit-limit-label {
      font-size: 0.7rem;
      color: #f97316;
      font-weight: 600;
      letter-spacing: 0.2px;
    }

    .credit-label {
      font-size: 0.55rem;
      color: #f97316;
      font-weight: 600;
      letter-spacing: 0.3px;
      line-height: 1;
    }

    .topup-section {
      display: flex;
      align-items: center;
      flex-shrink: 0;
    }

    .topup-btn {
      padding: 0.5rem 1.25rem;
      font-weight: 600;
      font-size: 0.875rem;
      letter-spacing: 0.3px;
      border-radius: 8px;
      transition: all 0.2s ease;
    }

    .topup-btn:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(124, 58, 237, 0.15);
    }

    .user-profile-section {
      display: flex;
      align-items: center;
      gap: 1rem;
      flex-shrink: 0;
      margin-left: auto;
    }

    .mobile-layout .user-profile-section {
      margin-left: 0;
      padding: 0.75rem;
      background: #faf8ff;
      border-radius: 8px;
    }

    .profile-text {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 0.25rem;
    }

    .mobile-layout .profile-text {
      align-items: flex-start;
    }

    .profile-name {
      font-size: 0.9rem;
      font-weight: 600;
      color: #1a003d;
      letter-spacing: 0.2px;
    }

    .profile-role {
      font-size: 0.75rem;
      color: #9ca3af;
      font-weight: 500;
      text-transform: capitalize;
    }

    .mobile-menu {
      position: absolute;
      top: calc(100% + 8px);
      right: 1rem;
      padding: 1.5rem;
      background: #ffffff;
      border: 1.5px solid #f0eafa;
      border-radius: 12px;
      z-index: 1000;
      width: calc(100% - 2rem);
      max-width: 320px;
      box-shadow: 0 8px 32px rgba(109, 40, 217, 0.12);
    }

    .mobile-menu-toggle {
      padding: 0.25rem 0.5rem;
      border: none;
      background: transparent;
    }

    .avatar-circle {
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      background: linear-gradient(135deg, #7c3aed 0%, #f97316 100%);
      flex-shrink: 0;
      box-shadow: 0 2px 8px rgba(124, 58, 237, 0.35);
    }

    .avatar-initials {
      font-family: 'DM Sans', 'Segoe UI', sans-serif;
      font-size: 0.8125rem;
      font-weight: 700;
      color: #ffffff;
      letter-spacing: 0.03em;
      text-transform: uppercase;
      line-height: 1;
      user-select: none;
    }

    /* Modal */
    .modal-header-custom {
      background: #f5f3ff;
      padding: 1rem 1.5rem;
      border-bottom: 1px solid #ede9fb;
      display: flex;
      justify-content: flex-end;
    }

    .modal-body-custom {
      padding: 3rem 2rem;
      background: #ffffff;
    }

    .modal-title {
      font-size: 1.75rem;
      font-weight: 700;
      color: #2d1b69;
      text-align: center;
      margin-bottom: 0.5rem;
      letter-spacing: -0.5px;
    }

    .modal-subtitle {
      font-size: 1rem;
      color: #7c5ba8;
      text-align: center;
      margin-bottom: 2rem;
      font-weight: 500;
    }

    .modal-instructions {
      display: flex;
      justify-content: center;
    }

    .instructions-list {
      list-style: decimal;
      padding-left: 1.5rem;
      max-width: 480px;
      width: 100%;
    }

    .instructions-list li {
      color: #374151;
      margin-bottom: 1rem;
      line-height: 1.6;
      font-size: 0.95rem;
    }

    .instructions-list .fw-bold {
      color: #7c3aed;
      font-weight: 600;
    }

    @media (max-width: 768px) {
      .modal-body-custom { padding: 2rem 1.5rem; }
      .modal-title { font-size: 1.5rem; }
    }
  `],
})
export class UserInfoComponent implements OnInit {
  @Input() userProfile: IZikataClientProfile | null | undefined = null;
  @Input() userRole = '';
  @Input() avatarSrc = 'assets/images/Ellipse 1.svg';
  @Input() avatarSize = 36;
  /** When true, renders the slim horizontal compact bar for the mobile fixed header */
  @Input() mobileCompactMode = false;

  @ViewChild('content') private modalContent!: TemplateRef<any>;

  private selectedSenderSubject = new BehaviorSubject<SenderId | null>(null);
  private selectedOrgSubject = new BehaviorSubject<Organization | null>(null);

  selectedOrg$ = this.selectedOrgSubject.asObservable();

  private isMobileSubject = new BehaviorSubject<boolean>(false);
  isMobile$ = this.isMobileSubject.asObservable();

  showMobileMenu = false;
  viewModel$: Observable<ViewModel>;
  currentSender: SenderId | null = null;
  currentSenderIdBalance: number | null = null;
  member: Member | null | undefined = null;

  constructor(
    private modalService: NgbModal,
    private userInfoConfigService: UserInfoConfigService,
    private senderStateService: SenderStateService,
    private orgStateService: OrgStateService
  ) {
    this.checkScreenSize();
    this.viewModel$ = this.initViewModel();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkScreenSize();
  }

  checkScreenSize() {
    this.isMobileSubject.next(window.innerWidth < 768);
    if (window.innerWidth >= 768) {
      this.showMobileMenu = false;
    }
  }

  toggleMobileMenu() {
    this.showMobileMenu = !this.showMobileMenu;
  }

  getInitials(): string {
    const first = (this.userProfile?.firstName ?? this.member?.firstName ?? '').trim();
    const last  = (this.userProfile?.lastName  ?? '').trim();
    if (first && last)  return `${first[0]}${last[0]}`;
    if (first)          return first.substring(0, 2);
    return '?';
  }

  ngOnInit() {
    this.currentSenderIdBalance = null;
    const currentOrg = this.orgStateService.getCurrentOrg();
    if (currentOrg) {
      this.loadMember(this.userProfile, currentOrg.id);
      this.selectedOrgSubject.next(currentOrg);
    } else {
      this.initializeOrg();
    }
  }

  private initViewModel(): Observable<ViewModel> {
    return combineLatest({
      config: this.userInfoConfigService.getConfig(),
      orgs: this.userInfoConfigService.getOrgs(),
      selectedOrg: this.selectedOrg$,
      isMobile: this.isMobile$,
    }).pipe(
      map(({ config, orgs, selectedOrg, isMobile }) => ({
        config, orgs, selectedOrg, isMobile,
      }))
    );
  }

  private initializeOrg() {
    this.userInfoConfigService.getOrgs().pipe(take(1)).subscribe((orgs) => {
      if (orgs.length > 0) {
        this.orgSelected(orgs[0]);
        this.loadMember(this.userProfile, orgs[0].id);
      }
    });
  }

  senderIdSelected(sender: SenderId) {
    this.selectedSenderSubject.next(sender);
    this.senderStateService.setCurrentSender(sender);
    if (this.isMobileSubject.getValue()) this.showMobileMenu = false;
    this.currentSender = sender;
    this.userInfoConfigService.getBalance(sender.id).subscribe({
      next: (balance) => (this.currentSenderIdBalance = balance),
      error: (error) => console.error('Error loading balance:', error),
    });
  }

  loadBalance() {
    const currentSender = this.selectedSenderSubject.getValue();
    if (currentSender) {
      this.userInfoConfigService.getBalance(currentSender.id).subscribe({
        next: (balance) => {
          this.currentSenderIdBalance = balance;
          this.selectedSenderSubject.next({ ...currentSender });
        },
        error: (error) => console.error('Error loading balance:', error),
      });
    }
  }

  openTopUpModal(selectedSender: SenderId | null) {
    if (selectedSender) {
      if (this.isMobileSubject.getValue()) this.showMobileMenu = false;
      const modalRef: NgbModalRef = this.modalService.open(this.modalContent, { centered: true, size: 'lg' });
      modalRef.result.then(
        (result: any) => console.log('Modal closed:', result),
        (reason: any) => console.log('Modal dismissed:', reason)
      );
    } else {
      this.showSenderSelectionNotification();
    }
  }

  private showSenderSelectionNotification() {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn { from { transform: translateX(400px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
      @keyframes slideOut { to { transform: translateX(400px); opacity: 0; } }
      .sender-notification { position: fixed; top: 24px; right: 24px; z-index: 10000; animation: slideIn 0.3s cubic-bezier(0.34,1.56,0.64,1); font-family: 'DM Sans','Segoe UI',sans-serif; }
      .sender-notification.fade-out { animation: slideOut 0.3s ease-out forwards; }
      .notification-content { display: flex; align-items: flex-start; gap: 1rem; padding: 1rem 1.5rem; background: #ffffff; border-left: 4px solid #ff6b35; border-radius: 8px; box-shadow: 0 4px 24px rgba(255,107,53,0.15); max-width: 420px; min-width: 280px; }
      .notif-icon { width: 24px; height: 24px; flex-shrink: 0; color: #ff6b35; margin-top: 2px; }
      .notif-text { display: flex; flex-direction: column; gap: .25rem; flex: 1; }
      .notif-title { font-size: .95rem; font-weight: 600; color: #1a003d; margin: 0; }
      .notif-msg { font-size: .85rem; color: #6b7280; line-height: 1.5; margin: 0; }
      .notif-close { display: flex; align-items: center; justify-content: center; width: 24px; height: 24px; padding: 0; background: none; border: none; cursor: pointer; color: #9ca3af; flex-shrink: 0; }
      .notif-close:hover { color: #ff6b35; }
      @media (max-width: 480px) { .sender-notification { top: 12px; right: 12px; left: 12px; } .notification-content { min-width: auto; max-width: 100%; } }
    `;
    document.head.appendChild(style);

    const notification = document.createElement('div');
    notification.className = 'sender-notification';
    notification.innerHTML = `
      <div class="notification-content">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="notif-icon">
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        <div class="notif-text">
          <div class="notif-title">Select a Sender ID</div>
          <div class="notif-msg">Please choose a Sender ID to proceed with top-up</div>
        </div>
        <button class="notif-close" type="button" aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    `;

    const closeBtn = notification.querySelector('.notif-close');
    closeBtn?.addEventListener('click', () => {
      notification.classList.add('fade-out');
      setTimeout(() => notification.remove(), 300);
    });
    document.body.appendChild(notification);
    setTimeout(() => {
      if (notification.parentElement) {
        notification.classList.add('fade-out');
        setTimeout(() => notification.remove(), 300);
      }
    }, 4000);
  }

  protected orgSelected(organization: Organization) {
    this.selectedOrgSubject.next(organization);
    this.orgStateService.setCurrentOrg(organization);
    if (this.isMobileSubject.getValue()) this.showMobileMenu = false;
    if (organization.senderIds.length > 0) {
      this.senderIdSelected(organization.senderIds[0]);
    } else {
      this.selectedSenderSubject.next(null);
      this.currentSender = null;
    }
    organization.members.forEach((member) => {
      if (member.userId === this.userProfile?.id) this.userRole = member.role;
    });
  }

  private loadMember(userProfile: IZikataClientProfile | null | undefined, organizationId: string) {
    if (userProfile?.id) {
      this.userInfoConfigService.getMemberRole(organizationId, userProfile.id)
        .subscribe({ next: (res) => (this.member = res) });
    }
  }
}